import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, MenuItem, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography, Button, CircularProgress,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { SupplierService } from '../../services/supplier.service';
import { SupplierItemService } from '../../services/supplierItem.service';
import { ItemService } from '../../services/item.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { SupplierItem } from '../../interfaces/procurement/supplierItem.interface';
import { Item } from '../../interfaces/item.interface';
import { useToast } from '../../context/ToastContext';

export const SupplierCatalogueBrowse: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [itemId, setItemId] = useState('');
  const [rows, setRows] = useState<SupplierItem[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<string[]>([]);
  const [priceAsc, setPriceAsc] = useState(true);
  const [loadingBoot, setLoadingBoot] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);

  useEffect(() => {
    Promise.all([
      ItemService.getItemsByInstitution(0, 200).then(r => r.content),
      SupplierService.listByInstitution(0, 500).then(r => r.content),
    ])
      .then(([its, sups]) => { setItems(its); setSuppliers(sups); })
      .catch(e => toast.showApiError(e, 'Failed to load catalogue prerequisites'))
      .finally(() => setLoadingBoot(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!itemId) { setRows([]); return; }
    setLoadingRows(true);
    SupplierItemService.listByItem(itemId, 0, 50)
      .then((page: { content: SupplierItem[] }) => setRows(page.content))
      .catch(e => toast.showApiError(e, 'Failed to load catalogue for item'))
      .finally(() => setLoadingRows(false));
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const supplierName = (id: string) => suppliers.find(s => s.id === id)?.name ?? id;

  const visibleRows = useMemo(() => {
    const filtered = supplierFilter.length === 0
      ? rows
      : rows.filter(r => supplierFilter.includes(r.supplierId));
    return [...filtered].sort((a, b) => {
      if (a.preferred !== b.preferred) return a.preferred ? -1 : 1;
      return priceAsc ? a.unitPrice - b.unitPrice : b.unitPrice - a.unitPrice;
    });
  }, [rows, supplierFilter, priceAsc]);

  const onUseForNextPO = (supplierId: string) => {
    navigate(`/procurement/purchase-orders/new?supplierId=${supplierId}&itemId=${itemId}`);
  };

  if (loadingBoot) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Supplier Catalogue</Typography>
      <Paper sx={{ p: 3, mb: 2 }}>
        <TextField
          select
          fullWidth
          label="Item"
          value={itemId}
          onChange={e => setItemId(e.target.value)}
          inputProps={{ 'aria-label': 'Item' }}
        >
          {items.map(i => <MenuItem key={i.id} value={i.id}>{i.itemName}</MenuItem>)}
        </TextField>
      </Paper>

      {!itemId && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">Pick an item to see suppliers carrying it.</Typography>
        </Paper>
      )}

      {itemId && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              SelectProps={{ multiple: true, renderValue: (sel) => (sel as string[]).map(supplierName).join(', ') }}
              label="Filter suppliers"
              value={supplierFilter}
              onChange={e => setSupplierFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))}
              sx={{ minWidth: 240 }}
              inputProps={{ 'aria-label': 'Filter suppliers' }}
            >
              {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            {supplierFilter.length > 0 && (
              <Button size="small" onClick={() => setSupplierFilter([])}>Clear filter</Button>
            )}
            <Button size="small" onClick={() => setPriceAsc(v => !v)}>
              Price: {priceAsc ? 'low → high' : 'high → low'}
            </Button>
          </Box>

          {loadingRows ? <CircularProgress size={20} /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.preferred && <StarIcon fontSize="small" aria-label="preferred" />}</TableCell>
                    <TableCell>{supplierName(r.supplierId)}</TableCell>
                    <TableCell>{r.unitPrice.toFixed(4)}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => onUseForNextPO(r.supplierId)}>
                        Use for next PO
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Chip label="No suppliers carry this item" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SupplierCatalogueBrowse;
